/* ==========================================================================
   LF: CB2-style collection/search grid — shared behaviour
   Extracted from lf-cl2-products.liquid so the collection grid and the search
   results grid share one implementation instead of two copies that drift.

   Self-initialising: every element carrying [data-lf-cl2-root] on the page is
   wired independently. The root supplies its own configuration via data
   attributes, so this file contains no Liquid:
     data-lf-cl2-root      - marker + wiring scope
     data-active-filters   - number of currently applied filter values
   Document-level delegation (Quick Add) is bound exactly once regardless of
   how many roots exist.
   ========================================================================== */
(function () {
  'use strict';

  /* ---- Sticky toolbar offset -------------------------------------------
     The filter bar pins below whatever sticky header the page has. Rather
     than hardcode a height (logo size is a theme setting and the header
     reflows at small widths), find the sticky element and measure it. */
  function measureStickyTop(root) {
    var offset = 0;
    var candidates = document.querySelectorAll('[id^="shopify-section"], header, sticky-header');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (root.contains(el) || el.contains(root)) continue;
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') continue;
      if (cs.visibility === 'hidden' || cs.display === 'none') continue;
      // Where this bar's bottom edge lands once pinned = its own `top` offset
      // plus its height. Deliberately NOT getBoundingClientRect().top: at
      // scroll 0 the header sits *below* the announcement bar, so reading its
      // live position measures 0 and the filter bar would later stick
      // underneath the header and disappear.
      var topVal = parseFloat(cs.top);
      if (isNaN(topVal) || topVal < 0 || topVal > 200) continue;
      var h = el.getBoundingClientRect().height;
      // Ignore full-height fixed overlays (cart drawer, menu drawer); a site
      // header is never a large fraction of the viewport.
      if (h <= 0 || h > window.innerHeight * 0.4) continue;
      offset = Math.max(offset, topVal + h);
    }
    root.style.setProperty('--lf-sticky-top', offset + 'px');
  }

  /* ---- Filters sidebar -------------------------------------------------- */
  function wireSidebar(root) {
    var layout = root.querySelector('[data-lf-layout]');
    var toggle = root.querySelector('[data-lf-toggle]');
    var sidebar = root.querySelector('[data-lf-sidebar]');
    if (!layout || !toggle) return;
    var storageKey = 'lfCl2FiltersOpen-' + (root.id || 'lf');

    function setOpen(open) {
      layout.classList.toggle('filters-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      try {
        if (open) { sessionStorage.setItem(storageKey, '1'); }
        else { sessionStorage.removeItem(storageKey); }
      } catch (e) {}
    }

    toggle.addEventListener('click', function () {
      setOpen(!layout.classList.contains('filters-open'));
    });

    if (sidebar) {
      var closeBtn = sidebar.querySelector('[data-lf-cl2-close]');
      if (closeBtn) closeBtn.addEventListener('click', function () { setOpen(false); });
    }

    // Re-open automatically if the panel was open when a filter checkbox
    // triggered this page reload, or if any filter is already active.
    var hadActive = parseInt(root.getAttribute('data-active-filters'), 10) > 0;
    var wasOpen = false;
    try { wasOpen = sessionStorage.getItem(storageKey) === '1'; } catch (e) {}
    if (wasOpen || hadActive) setOpen(true);

    // Persist the open state across the reload a sidebar checkbox causes.
    var boxes = sidebar ? sidebar.querySelectorAll('input[type="checkbox"]') : [];
    for (var i = 0; i < boxes.length; i++) {
      boxes[i].addEventListener('change', function () {
        try { sessionStorage.setItem(storageKey, '1'); } catch (e) {}
      });
    }
  }

  /* ---- Toolbar box dropdowns -------------------------------------------- */
  var LF_CL2_MOBILE_MQ = '(max-width: 900px)';

  /* Below 900px .lf-cl2-toolbar carries `overflow-x: auto` so the button row
     can scroll horizontally, and that forces `overflow-y` to `auto` too
     (CSS Overflow spec), clipping a `position: absolute` panel to the
     toolbar's own short row instead of letting it float over the page.

     v25 r11 — `position: fixed` alone did NOT fix that, and the reason is
     worth writing down. Two things were still working against the panel:

       1. WebKit on iOS keeps clipping a fixed-position descendant to its
          nearest scroll container, and the toolbar is one.
       2. .lf-cl2-toolbar is its own stacking context — `position: sticky`
          plus `z-index: 50` — and the mobile block resets only `position`,
          not `z-index`. Everything inside it therefore paints at layer 50
          however high the panel's own z-index goes, which is why the part
          of the panel that did escape still sat under the product grid.

     Reported symptom, and it matches both: the Sort menu showed two of its
     four options and the rest disappeared behind the products.

     The fix is to stop fighting the ancestors and leave them — an open
     panel is moved to <body> and put back on close. A body child has no
     clipping ancestor and no inherited stacking context. This is only safe
     because no panel lives inside a <form>: the filter checkboxes carry
     inline onchange handlers that navigate, and the sort options are plain
     <a> links, so neither depends on where it sits in the DOM.

     Desktop (and any panel while closed) is untouched — this only runs at
     mobile widths, and resetPanelPosition returns the panel and clears
     every inline style the rest of the time, so nothing leaks into the
     desktop absolute-positioning layout. */
  function isMobileToolbar() {
    return window.matchMedia(LF_CL2_MOBILE_MQ).matches;
  }

  function positionPanelForMobile(btn, panel) {
    if (!isMobileToolbar()) { resetPanelPosition(panel); return; }

    /* Take the panel out of the toolbar for as long as it is open. A child
       of <body> has no clipping ancestor and no inherited stacking context,
       so neither the scroller nor the toolbar's z-index can touch it. */
    if (panel.parentNode !== document.body) {
      if (!panel.lfHome) panel.lfHome = panel.parentNode;
      document.body.appendChild(panel);
      panel.setAttribute('data-lf-portal', 'true');
    }

    /* Once the panel is a child of <body>, the
       `.lf-cl2-box-dd.open .lf-cl2-box-panel { display: block }` rule no
       longer matches it — the panel is no longer a descendant of the open
       dropdown — so display is set here rather than left to CSS. */
    panel.style.display = 'block';
    panel.style.position = 'fixed';
    panel.style.right = 'auto';
    panel.style.zIndex = '1000';
    /* Cleared before measuring: a max-height left over from the previous
       open would make offsetHeight report the capped height, not the real
       one, and the placement decision below needs the real one. */
    panel.style.maxHeight = '';
    panel.style.overflowY = '';

    var margin = 12;
    var gap = 6;
    var r = btn.getBoundingClientRect();
    var w = panel.offsetWidth || 230;
    var h = panel.offsetHeight;

    var left = Math.min(r.left, window.innerWidth - w - margin);
    panel.style.left = Math.max(left, margin) + 'px';

    /* Prefer below the button, flip above only when there is genuinely more
       room there, and cap the height either way so a long filter list
       scrolls inside itself instead of running off the bottom of the screen
       — which is what hid two of the four Sort options. */
    var below = window.innerHeight - r.bottom - gap - margin;
    var above = r.top - gap - margin;
    var top;
    if (h <= below || below >= above) {
      top = r.bottom + gap;
      if (h > below) {
        panel.style.maxHeight = Math.max(below, 140) + 'px';
        panel.style.overflowY = 'auto';
      }
    } else {
      var used = Math.min(h, above);
      if (h > above) {
        used = Math.max(above, 140);
        panel.style.maxHeight = used + 'px';
        panel.style.overflowY = 'auto';
      }
      top = Math.max(margin, r.top - gap - used);
    }
    panel.style.top = top + 'px';
  }

  function resetPanelPosition(panel) {
    if (panel.getAttribute('data-lf-portal') === 'true') {
      panel.removeAttribute('data-lf-portal');
      if (panel.lfHome && document.contains(panel.lfHome)) {
        panel.lfHome.appendChild(panel);
      } else if (panel.parentNode) {
        /* Its section was re-rendered out from under it (theme editor). */
        panel.parentNode.removeChild(panel);
      }
    }
    panel.style.display = '';
    panel.style.position = '';
    panel.style.top = '';
    panel.style.left = '';
    panel.style.right = '';
    panel.style.maxHeight = '';
    panel.style.overflowY = '';
    panel.style.zIndex = '';
  }

  /* shopify:section:load re-runs init() without a page load. A panel that
     was open and portaled at that moment is still parented to <body> while
     its section is gone, so it would sit on the page forever. */
  function cleanupOrphanPortals() {
    document.querySelectorAll('body > .lf-cl2-box-panel[data-lf-portal]').forEach(
      function (panel) { resetPanelPosition(panel); }
    );
  }

  function wireDropdowns(root) {
    var dds = root.querySelectorAll('[data-lf-box-dd]');
    if (!dds.length) return;

    /* Every lookup goes through this list rather than through
       dd.querySelector('.lf-cl2-box-panel'): once a panel is portaled to
       <body> it is no longer a descendant of its dropdown, and a live
       querySelector would return null exactly when the panel needs closing. */
    var entries = [];

    dds.forEach(function (dd) {
      var btn = dd.querySelector('.lf-cl2-box-btn');
      var panel = dd.querySelector('.lf-cl2-box-panel');
      if (!btn || !panel) return;
      panel.lfHome = dd;
      entries.push({ dd: dd, btn: btn, panel: panel });
    });
    if (!entries.length) return;

    function closeEntry(entry) {
      entry.dd.classList.remove('open');
      entry.btn.setAttribute('aria-expanded', 'false');
      resetPanelPosition(entry.panel);
    }

    entries.forEach(function (entry) {
      entry.btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = entry.dd.classList.contains('open');
        entries.forEach(function (other) {
          if (other !== entry) closeEntry(other);
        });
        if (isOpen) {
          closeEntry(entry);
        } else {
          entry.dd.classList.add('open');
          entry.btn.setAttribute('aria-expanded', 'true');
          /* .open has to be applied before the panel is measured — until
             then it is still display:none and reports no size at all. */
          positionPanelForMobile(entry.btn, entry.panel);
        }
      });
    });

    /* A portaled panel is outside its own dropdown, so dd.contains() alone
       would read a tap on a filter checkbox or a sort link as an outside
       click and close the menu under the finger. */
    document.addEventListener('click', function (e) {
      entries.forEach(function (entry) {
        if (!entry.dd.classList.contains('open')) return;
        if (entry.dd.contains(e.target) || entry.panel.contains(e.target)) return;
        closeEntry(entry);
      });
    });

    /* The toolbar is not sticky on mobile, so scrolling moves the trigger
       button while a fixed panel stays where it was measured. Re-measure on
       an animation frame (scroll fires far faster than the browser paints),
       and close outright once the button has left the viewport, rather than
       leaving a menu floating over the page with nothing to belong to. */
    var pending = false;
    function reposition() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        entries.forEach(function (entry) {
          if (!entry.dd.classList.contains('open')) return;
          var r = entry.btn.getBoundingClientRect();
          if (isMobileToolbar() && (r.bottom < 0 || r.top > window.innerHeight)) {
            closeEntry(entry);
            return;
          }
          positionPanelForMobile(entry.btn, entry.panel);
        });
      });
    }
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, { passive: true });
  }

  /* ---- View More Products ----------------------------------------------
     Fetches the next paginated page of this same URL (so active filters, the
     search term and sort are all inherited automatically), lifts the product
     cards out of it and appends them, then advances the counter, the progress
     bar and the stored next-page URL. */
  function wireLoadMore(root) {
    var wrap = root.querySelector('[data-lf-more]');
    if (!wrap) return;
    var btn = wrap.querySelector('[data-lf-more-btn]');
    var shownEl = wrap.querySelector('[data-lf-shown]');
    var barEl = wrap.querySelector('[data-lf-bar]');
    var progressEl = wrap.querySelector('.lf-cl2-more-progress');
    var grid = root.querySelector('.lf-cl2-grid');
    var total = parseInt(wrap.dataset.total, 10) || 0;
    if (!btn || !grid) return;

    btn.addEventListener('click', function () {
      var nextUrl = wrap.dataset.nextUrl;
      if (!nextUrl || btn.disabled) return;
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Loading...';
      fetch(nextUrl)
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var nextGrid = doc.querySelector('.lf-cl2-grid');
          var added = nextGrid ? nextGrid.querySelectorAll('.lf-cl2-card') : [];
          for (var i = 0; i < added.length; i++) grid.appendChild(added[i]);

          var shown = grid.querySelectorAll('.lf-cl2-card').length;
          if (shownEl) shownEl.textContent = shown;
          if (barEl) barEl.style.width = (total ? (shown / total) * 100 : 100) + '%';
          if (progressEl) progressEl.setAttribute('aria-valuenow', shown);

          // The fetched page carries its own "next" pointer; when it has none
          // we've reached the end and the button retires.
          var nextMore = doc.querySelector('[data-lf-more]');
          var newNext = nextMore ? nextMore.dataset.nextUrl : '';
          wrap.dataset.nextUrl = newNext || '';
          if (!newNext) { wrap.setAttribute('data-exhausted', ''); }
          else { btn.disabled = false; }
          btn.textContent = label;
        })
        .catch(function () {
          btn.textContent = 'Could not load — try again';
          btn.disabled = false;
          setTimeout(function () { btn.textContent = label; }, 2500);
        });
    });
  }

  /* ---- Quick Add (document-level, bound once) ---------------------------
     Same cart.js / cart-drawer / analytics flow as the rest of the theme's
     quick-add buttons. */
  function bindQuickAddOnce() {
    if (window.__lfCl2QuickAddBound) return;
    window.__lfCl2QuickAddBound = true;

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.lf-cl2-quickadd');
      if (!btn) return;
      e.preventDefault();
      var vid = btn.dataset.variantId;
      if (!vid) return;
      var orig = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding...';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(vid), quantity: 1 }] })
      })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function () {
          btn.textContent = 'Added';
          try {
            var pid = btn.dataset.productId, vidInt = parseInt(vid);
            if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && window.ShopifyAnalytics.lib.track) {
              window.ShopifyAnalytics.lib.track('Added Product', {
                currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
                quantity: 1, variantId: vidInt, productId: parseInt(pid),
                productGid: 'gid://shopify/Product/' + pid,
                variantGid: 'gid://shopify/ProductVariant/' + vidInt,
                name: btn.dataset.productTitle || '', price: btn.dataset.variantPrice || '',
                sku: btn.dataset.variantSku || '', brand: btn.dataset.productVendor || '',
                variant: btn.dataset.variantTitle || '', category: btn.dataset.productType || ''
              });
            }
          } catch (err) {}
          fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble')
            .then(function (r) { return r.json(); })
            .then(function (sections) {
              var dEl = document.querySelector('cart-drawer');
              if (dEl && typeof dEl.renderContents === 'function') dEl.renderContents({ sections: sections });
              setTimeout(function () {
                if (dEl) {
                  dEl.classList.remove('is-empty');
                  if (typeof dEl.open === 'function') {
                    try { dEl.open(); } catch (ex) { dEl.classList.add('active'); }
                  }
                }
              }, 300);
            });
          setTimeout(function () { btn.disabled = false; btn.textContent = orig; }, 2000);
        })
        .catch(function () {
          btn.textContent = 'Error';
          setTimeout(function () { btn.disabled = false; btn.textContent = orig; }, 1500);
        });
    });
  }

  function init() {
    bindQuickAddOnce();
    var roots = document.querySelectorAll('[data-lf-cl2-root]');
    roots.forEach(function (root) {
      if (root.__lfCl2Wired) return;
      root.__lfCl2Wired = true;
      measureStickyTop(root);
      wireSidebar(root);
      cleanupOrphanPortals();
      wireDropdowns(root);
      wireLoadMore(root);
      window.addEventListener('resize', function () { measureStickyTop(root); });
      window.addEventListener('load', function () { measureStickyTop(root); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Shopify theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', init);
})();
